import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AlertTriangle, CheckCircle2, MapPin, Lock } from "lucide-react";
import MobileNumpad, { useIsMobile } from "@/components/MobileNumpad";
import DeviationPhotoPicker from "@/components/DeviationPhotoPicker";
import TemperatureOcrField from "@/components/TemperatureOcrField";

export default function LogReading() {
  const { activeSiteId, isAllSites, isSingleSite, activeSite } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [equipment, setEquipment] = useState([]);
  const [types, setTypes] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [showDevModal, setShowDevModal] = useState(false);
  const [rootCause, setRootCause] = useState("");
  const [corrective, setCorrective] = useState("");
  const [immediate, setImmediate] = useState("");
  const [detectedAt, setDetectedAt] = useState("");
  const [devPhotos, setDevPhotos] = useState([]);
  const [photoErr, setPhotoErr] = useState("");
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [reworkTasks, setReworkTasks] = useState([]);
  const [activeRework, setActiveRework] = useState(null);
  const [rwCorrective, setRwCorrective] = useState("");
  const [rwNotes, setRwNotes] = useState("");
  const [rwValue, setRwValue] = useState("");
  const [rwBusy, setRwBusy] = useState(false);
  const [rwErr, setRwErr] = useState("");
  const isMobile = useIsMobile();
  const forceKeypad = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("keypad") === "1";
  const useCustomPad = isMobile || forceKeypad;
  const [showKeypad, setShowKeypad] = useState(false);
  const valueRef = useRef(null);
  // Temperature-OCR trail — populated only when the operator uses the
  // Snap-thermometer flow. `value` above is still authoritative; we just
  // stash the source photo path + LLM confidence for the audit trail.
  const [ocr, setOcr] = useState(null); // { photo_path, parsed_value, confidence }

  useEffect(() => {
    (async () => {
      try {
        const [s, e, t] = await Promise.all([
          api.get("/sites"),
          api.get("/equipment"),
          api.get("/equipment-types"),
        ]);
        setSites(s.data.sites || []);
        setEquipment(e.data.equipment || []);
        setTypes(t.data.equipment_types || []);
      } catch (ex) {
        setErr(formatErr(ex.response?.data?.detail) || "Failed to load equipment data");
      }
    })();
    loadRework();
    // eslint-disable-next-line
  }, [activeSiteId]);

  const loadRework = async () => {
    try {
      const { data } = await api.get("/rework-tasks");
      setReworkTasks(data.tasks || []);
    } catch { setReworkTasks([]); }
  };

  const openRework = (task) => {
    setActiveRework(task);
    setRwCorrective(""); setRwNotes(""); setRwValue(""); setRwErr("");
  };

  const submitRework = async () => {
    if (!rwCorrective.trim()) { setRwErr("A corrective action is required."); return; }
    setRwBusy(true); setRwErr("");
    try {
      await api.post(`/deviations/${activeRework.id}/rework`, {
        corrective_action: rwCorrective,
        notes: rwNotes || undefined,
        new_value: rwValue !== "" ? Number(rwValue) : undefined,
      });
      setActiveRework(null);
      loadRework();
    } catch (e) {
      setRwErr(formatErr(e.response?.data?.detail) || "Failed to submit rework");
    } finally { setRwBusy(false); }
  };

  // Keep the local site filter in sync with the global site context.
  useEffect(() => {
    if (isSingleSite && activeSiteId && activeSiteId !== "all") {
      setSiteId(activeSiteId); setEquipmentId("");
    } else if (!isAllSites && activeSiteId !== "all" && siteId !== activeSiteId) {
      setSiteId(activeSiteId); setEquipmentId("");
    } else if (isAllSites && siteId !== "") {
      setSiteId(""); setEquipmentId("");
    }
    // eslint-disable-next-line
  }, [activeSiteId, isAllSites, isSingleSite]);

  // QR deep-link from Vehicle Scan (Cold Truck → Log temperature). URL
  // shape: /app/log?equipmentId=<id>. Pre-selects the vehicle + its
  // site + strips the query param so a refresh doesn't re-fire the
  // pre-select. Waits until equipment has loaded so the id resolves.
  useEffect(() => {
    const qEq = searchParams.get("equipmentId");
    if (!qEq || equipment.length === 0) return;
    const match = equipment.find((e) => e.id === qEq);
    if (match) {
      setEquipmentId(qEq);
      if (match.site_id) setSiteId(match.site_id);
      const next = new URLSearchParams(searchParams);
      next.delete("equipmentId");
      setSearchParams(next, { replace: true });
    }
  }, [equipment, searchParams, setSearchParams]);

  const selectedEq = useMemo(
    () => equipment.find((e) => e.id === equipmentId),
    [equipment, equipmentId]
  );
  const selectedType = useMemo(
    () => selectedEq ? types.find((t) => t.id === selectedEq.equipment_type_id) : null,
    [selectedEq, types]
  );
  const siteEquipment = useMemo(
    () => siteId ? equipment.filter((e) => e.site_id === siteId) : equipment,
    [equipment, siteId]
  );

  const unit = selectedType?.parameter_unit || "";
  const paramName = selectedType?.parameter_name || "Value";
  const minV = selectedType?.min_value;
  const maxV = selectedType?.max_value;
  const hasRange = !!selectedType?.has_operating_range && minV !== null && maxV !== null && minV !== undefined && maxV !== undefined;
  const isException = selectedEq?.monitoring_mode === "exception";
  const isDev = hasRange && value !== "" && (Number(value) < minV || Number(value) > maxV);
  // OCR is only meaningful for temperature parameters. We detect this
  // by unit — anything that includes a °C / °F / C / F suffix qualifies.
  // Values with no unit or unusual units (e.g. pH, %RH) skip the OCR
  // slot entirely.
  const isTempParam = hasRange && /^°?[CF]$/i.test((unit || "").trim());

  const reset = () => {
    setValue(""); setNotes(""); setRootCause(""); setCorrective(""); setImmediate("");
    setDetectedAt(""); setDevPhotos([]); setPhotoErr("");
    setOcr(null);
  };

  const trySubmit = (e) => {
    e.preventDefault();
    setErr("");
    if (!selectedEq) { setErr("Pick an equipment first"); return; }
    if (!hasRange) {
      setErr("This equipment's type has no operating range configured. Ask your admin to enable operating parameters on the equipment type.");
      return;
    }
    // Only out-of-range values need corrective action. In exception mode an
    // in-range value means the unit recovered — record it as a normal reading.
    if (isDev) { setShowDevModal(true); return; }
    doSubmit();
  };

  const doSubmit = async () => {
    setBusy(true);
    // Clear any prior error so a fresh success can't show alongside a
    // stale red pill (and vice versa — set in trySubmit/catch below).
    setErr("");
    setPhotoErr("");
    try {
      // OCR trail — only sent when the operator used Snap. `ocr_edited`
      // is stamped true when the submitted value drifted from the parsed
      // value beyond a small float tolerance.
      const ocrPayload = ocr ? {
        ocr_photo_path: ocr.photo_path,
        ocr_parsed_value: ocr.parsed_value,
        ocr_confidence: ocr.confidence,
        ocr_edited: Math.abs(Number(value) - Number(ocr.parsed_value)) > 0.05,
      } : {};
      const { data } = await api.post("/readings/by-equipment", {
        equipment_id: equipmentId,
        value: Number(value),
        notes,
        root_cause: rootCause || undefined,
        corrective_action: corrective || undefined,
        immediate_action_taken: immediate || undefined,
        detected_at: isException ? new Date(detectedAt || new Date().toISOString().slice(0, 10)).toISOString() : undefined,
        ...ocrPayload,
      });
      // Upload evidence photos AFTER the reading is created — the endpoint
      // needs the reading id to attach them. We tolerate partial failures:
      // any photo that fails is surfaced as a soft warning but the reading
      // itself (which is the compliance-critical part) still saves.
      const rid = data.reading?.id;
      if (rid && devPhotos.length && data.reading?.is_deviation) {
        const failed = [];
        for (const f of devPhotos) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/readings/${rid}/photo`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (pe) {
            failed.push(f.name || "photo");
          }
        }
        if (failed.length) {
          setPhotoErr(`Reading saved, but ${failed.length} photo${failed.length > 1 ? "s" : ""} failed to upload: ${failed.join(", ")}`);
        }
      }
      setSuccess(data.reading);
      setShowDevModal(false);
      reset();
    } catch (e) {
      setSuccess(null);
      setErr(formatErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl vb-fade-in" data-testid="log-page">
      <h1 className="vb-heading text-3xl mb-2" style={{ color: "var(--vb-text-2)" }}>
        {isException ? "Log Temperature Deviation" : "CCP Data Submission"}
      </h1>
      <p className="opacity-70 mb-6">
        {isException
          ? "This unit is monitored by an external IoT data-logger. Record a deviation here when the logger alarms — capture the out-of-range temperature you observed and the corrective action taken."
          : "Submit a CCP measurement for a piece of equipment. If the value is outside the equipment's operating range, you'll be prompted to record corrective action immediately."}
      </p>

      {reworkTasks.length > 0 && (
        <div className="vb-card p-5 mb-6" data-testid="rework-section" style={{ borderLeft: "4px solid var(--vb-error)" }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} style={{ color: "var(--vb-error)" }} />
            <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Rework tasks ({reworkTasks.length})</h2>
          </div>
          <p className="text-sm opacity-70 mb-3">A supervisor sent these deviations back for rework. Redo the corrective action and resubmit for reconfirmation.</p>
          <div className="grid gap-3">
            {reworkTasks.map((task) => (
              <div key={task.id} className="vb-card p-4" data-testid={`rework-task-${task.id}`} style={{ background: "var(--vb-bg)" }}>
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div>
                    <strong>{task.ccp_name}</strong>
                    <div className="text-sm opacity-80">
                      Reading <span style={{ color: "var(--vb-error)" }}>{task.value}{task.unit}</span> · range {task.min_value}–{task.max_value}{task.unit} · logged {new Date(task.logged_at).toLocaleDateString()}
                    </div>
                    {task.manager_review?.comments && (
                      <div className="text-sm mt-2 p-2 rounded" style={{ background: "var(--vb-card)", border: "1px solid var(--vb-border)" }}>
                        <span className="opacity-60 text-xs uppercase tracking-wider">Supervisor note:</span> {task.manager_review.comments}
                      </div>
                    )}
                  </div>
                  <button className="vb-btn text-sm" onClick={() => openRework(task)} data-testid={`rework-start-${task.id}`}>Start rework</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {success && (
        <div className="vb-pill ok mb-4" data-testid="log-success">
          <CheckCircle2 size={14} /> Saved · {success.value}{success.unit} · {success.is_deviation ? "deviation logged" : "within range"}
        </div>
      )}
      {photoErr && <div className="vb-pill warn mb-4" data-testid="log-photo-warn">{photoErr}</div>}
      {err && <div className="vb-pill err mb-4" data-testid="log-error">{err}</div>}

      <form onSubmit={trySubmit} className="vb-card p-6 grid gap-4">
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Site</label>
          {isSingleSite ? (
            <div className="vb-input flex items-center gap-2 opacity-90" data-testid="log-site-locked">
              <Lock size={14} className="opacity-70" /> {activeSite?.name || "Your site"}
            </div>
          ) : !isAllSites ? (
            <div className="vb-input flex items-center gap-2 opacity-90" data-testid="log-site-locked">
              <MapPin size={14} className="opacity-70" /> {activeSite?.name || ""}
            </div>
          ) : (
            <select className="vb-input" value={siteId} onChange={(e) => { setSiteId(e.target.value); setEquipmentId(""); }} data-testid="log-site">
              <option value="">All sites</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Equipment</label>
          <select className="vb-input" required value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} data-testid="log-equipment">
            <option value="">Select equipment…</option>
            {siteEquipment.map((e) => {
              const t = types.find((x) => x.id === e.equipment_type_id);
              return (
                <option key={e.id} value={e.id}>
                  {e.name}
                  {t?.name ? ` · ${t.name}` : ""}
                  {t?.has_operating_range ? ` (${t.min_value}–${t.max_value}${t.parameter_unit || ""})` : ""}
                </option>
              );
            })}
          </select>
          {siteEquipment.length === 0 && <p className="text-xs opacity-60 mt-1">No equipment yet for this site — add some on the Equipment page.</p>}
        </div>

        {selectedEq && !hasRange && (
          <div className="vb-pill err" data-testid="log-no-range">
            <AlertTriangle size={14} /> This equipment type has no operating range configured. Ask your admin to enable operating parameters.
          </div>
        )}
        {selectedEq && hasRange && (
          <div className="flex flex-wrap items-center gap-2" data-testid="log-range-row">
            <div className="vb-pill" style={{ background: "var(--vb-bg)" }} data-testid="log-range">
              {paramName} range: <strong>{minV}{unit} – {maxV}{unit}</strong>
            </div>
            {isException && (
              <div className="vb-pill warn" data-testid="log-exception-badge">
                <AlertTriangle size={13} /> Exception capture — logger-monitored unit
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
            {isException ? "Observed out-of-range value" : "Measured value"} {unit && `(${unit})`}
          </label>
          <div className="relative">
            <input
              ref={valueRef}
              className="vb-input"
              type="text"
              inputMode={useCustomPad ? "none" : "decimal"}
              pattern="-?(\d+\.?\d*|\.\d+)" required
              readOnly={useCustomPad}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onFocus={() => {
                if (!useCustomPad || !hasRange) return;
                setShowKeypad(true);
                setTimeout(() => valueRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 60);
              }}
              onClick={() => {
                if (!useCustomPad || !hasRange) return;
                setShowKeypad(true);
                setTimeout(() => valueRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 60);
              }}
              placeholder={hasRange ? `Enter ${paramName.toLowerCase()} in ${unit}` : "Enter measurement"}
              data-testid="log-value"
              style={{ paddingRight: unit ? 72 : undefined }}
            />
            {unit && (
              <span
                data-testid="log-unit-suffix"
                className="absolute top-1/2 right-3 -translate-y-1/2 text-xs font-semibold px-2 py-0.5 rounded"
                style={{ color: "var(--vb-text-2)", background: "var(--vb-bg)", pointerEvents: "none" }}
              >{unit}</span>
            )}
          </div>
          {isDev && (
            <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: "var(--vb-error)" }}>
              <AlertTriangle size={16} /> Out of range — corrective action will be required.
            </div>
          )}
          {isException && !isDev && value !== "" && (
            <div className="mt-2 flex items-center gap-2 text-sm" data-testid="log-inrange-ok" style={{ color: "var(--vb-accent)" }}>
              <CheckCircle2 size={16} /> Within operating range — no action required.
            </div>
          )}
          {isTempParam && (
            <div className="mt-3">
              <TemperatureOcrField
                context="ccp"
                minC={Number(minV)}
                maxC={Number(maxV)}
                onConfirm={(r) => {
                  setValue(r.temp_c.toString());
                  setOcr({ photo_path: r.ocr_photo_path,
                           parsed_value: r.ocr_parsed_value,
                           confidence: r.ocr_confidence });
                }}
                onFallbackToManual={() => setOcr(null)}
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Notes (optional)</label>
          <textarea className="vb-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="log-notes" />
        </div>

        <button type="submit" className="vb-btn" disabled={busy || !selectedEq || value === "" || !hasRange} data-testid="log-submit">
          {busy ? "Saving…" : isDev ? (isException ? "Log deviation" : "Continue with deviation") : "Submit reading"}
        </button>
      </form>

      {showDevModal && selectedEq && hasRange && createPortal((
        <div className="vb-modal-bg" data-testid="deviation-modal">
          <div className="vb-modal">
            <div className="vb-deviation-banner">
              <AlertTriangle size={20} />
              {isException
                ? <>Temperature deviation — {selectedEq.name} (safe range {minV}–{maxV}{unit})</>
                : <>Deviation detected — value {value}{unit} is outside {minV}–{maxV}{unit}</>}
            </div>
            <h2>{isException ? "Record deviation & corrective action" : "Document corrective action"}</h2>
            <p className="text-sm opacity-80 mb-4">
              {isException
                ? "The logger flagged this unit out of range. Record what you found and the action taken — ISO 22000 requires a documented root cause and corrective action for every deviation."
                : "ISO 22000 requires an immediate corrective action plus root-cause documentation for every deviation. This is non-negotiable — please complete the fields below before submitting."}
            </p>

            {isException && (
              <div className="mb-3">
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Date detected</label>
                <input type="date" className="vb-input" value={detectedAt || new Date().toISOString().slice(0, 10)}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDetectedAt(e.target.value)} data-testid="dev-detected-at" />
              </div>
            )}

            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Root cause *</label>
            <textarea className="vb-input mb-3" rows={2} required value={rootCause} onChange={(e) => setRootCause(e.target.value)} data-testid="dev-root-cause"
              placeholder="e.g. Walk-in fridge door left ajar by morning shift" />

            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Immediate action taken</label>
            <textarea className="vb-input mb-3" rows={2} value={immediate} onChange={(e) => setImmediate(e.target.value)} data-testid="dev-immediate"
              placeholder="e.g. Door closed, items checked, no spoilage observed" />

            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Corrective action *</label>
            <textarea className="vb-input mb-4" rows={3} required value={corrective} onChange={(e) => setCorrective(e.target.value)} data-testid="dev-corrective"
              placeholder="e.g. Brief team on door discipline; install door alarm; recheck temp in 30 min" />

            <div className="mb-4">
              <DeviationPhotoPicker files={devPhotos} onChange={setDevPhotos} disabled={busy} />
            </div>

            <div className="flex gap-3 justify-end">
              <button type="button" className="vb-btn ghost" onClick={() => setShowDevModal(false)} data-testid="dev-cancel">Cancel</button>
              <button type="button" className="vb-btn warn" onClick={doSubmit} disabled={busy || !rootCause || !corrective} data-testid="dev-submit">
                {busy ? "Submitting…" : isException ? "Log deviation" : "Submit deviation"}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
      {showKeypad && (
        <MobileNumpad
          value={value}
          unit={unit}
          onChange={setValue}
          onClose={() => setShowKeypad(false)}
        />
      )}

      {activeRework && createPortal((
        <div className="vb-modal-bg" data-testid="rework-modal">
          <div className="vb-modal">
            <h2 className="flex items-center gap-2"><AlertTriangle size={18} style={{ color: "var(--vb-error)" }} /> Rework — {activeRework.ccp_name}</h2>
            <div className="vb-card p-3 mb-3 text-sm" style={{ background: "var(--vb-bg)" }}>
              <div>Original reading: <strong style={{ color: "var(--vb-error)" }}>{activeRework.value}{activeRework.unit}</strong> (range {activeRework.min_value}–{activeRework.max_value}{activeRework.unit})</div>
              {activeRework.manager_review?.comments && (
                <div className="mt-2"><span className="opacity-60 text-xs uppercase tracking-wider">Supervisor asked to redo:</span> {activeRework.manager_review.comments}</div>
              )}
            </div>

            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>New corrective action *</label>
            <textarea className="vb-input mb-3" rows={3} value={rwCorrective} onChange={(e) => setRwCorrective(e.target.value)} data-testid="rework-corrective"
              placeholder="Describe the corrective action you performed for this rework" />

            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Notes (optional)</label>
            <textarea className="vb-input mb-3" rows={2} value={rwNotes} onChange={(e) => setRwNotes(e.target.value)} data-testid="rework-notes" />

            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Re-checked reading (optional) {activeRework.unit && `(${activeRework.unit})`}</label>
            <input type="number" step="any" className="vb-input mb-4" value={rwValue} onChange={(e) => setRwValue(e.target.value)} data-testid="rework-value"
              placeholder="e.g. current temperature after fixing" />

            {rwErr && <div className="vb-pill err mb-3">{rwErr}</div>}
            <div className="flex gap-3 justify-end">
              <button type="button" className="vb-btn ghost" onClick={() => setActiveRework(null)} data-testid="rework-cancel">Cancel</button>
              <button type="button" className="vb-btn" onClick={submitRework} disabled={rwBusy || !rwCorrective.trim()} data-testid="rework-submit">
                {rwBusy ? "Submitting…" : "Resubmit to supervisor"}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
